import type { CampaignDefinition, Lead, StudioSettings } from "./types";

export const defaultSettings: StudioSettings = {
  senderName: "Aiden",
  studioName: "Gwinnett Business Photography",
  market: "Gwinnett County, GA",
  offer:
    "A practical brand photo session for local business owners, realtors, and freelancers: updated headshots, working portraits, location photos, and a reusable image library for websites, Google Business Profiles, LinkedIn, Instagram, listing presentations, and referral marketing.",
  postalAddress: "",
  dailyMailboxLimit: 25,
  mailboxCount: 2,
  positiveReplyRate: 5,
};

export const campaigns: CampaignDefinition[] = [
  {
    id: "brand_refresh",
    label: "Realtor Brand Kit",
    angle: "Headshots, lifestyle portraits, and market-facing photos for agents and real estate teams",
    subject: "Photos for {{company}}",
    idealAccounts: [
      "Realtors and real estate agents",
      "Brokerages and real estate teams",
      "Mortgage brokers and closing attorneys",
      "Stagers, home organizers, and home service pros",
    ],
    triggers: [
      "realtor",
      "real estate",
      "agent",
      "broker",
      "listing",
      "buyer",
      "seller",
      "Zillow",
      "Google Business Profile",
      "Instagram",
      "referral",
      "headshot",
    ],
    valueProps: [
      "More credible first impression",
      "Photos for listings, social, and referral partners",
      "Stronger personal brand in a local market",
    ],
  },
  {
    id: "hiring",
    label: "Local Service Business",
    angle: "Website and Google profile photos for owner-led local companies",
    subject: "Updated business photos for {{company}}",
    idealAccounts: [
      "Med spas, salons, gyms, clinics, and studios",
      "Contractors, cleaners, organizers, and home service firms",
      "Insurance, tax, accounting, and legal offices",
      "Restaurants, boutiques, and local retailers",
    ],
    triggers: [
      "new location",
      "grand opening",
      "Google reviews",
      "before and after",
      "team",
      "service page",
      "outdated",
      "website",
      "social",
    ],
    valueProps: [
      "Better Google and website trust signals",
      "Real photos instead of stock images",
      "Reusable content for ads, social, and local search",
    ],
  },
  {
    id: "executive",
    label: "Freelancer Authority",
    angle: "Personal brand imagery for consultants, coaches, creatives, and solo operators",
    subject: "Personal brand photos for {{company}}",
    idealAccounts: [
      "Consultants, coaches, designers, and creatives",
      "Financial advisors, attorneys, and independent pros",
      "Speakers, creators, and referral-based freelancers",
      "Solo operators with active LinkedIn or Instagram",
    ],
    triggers: [
      "founder",
      "owner",
      "consultant",
      "coach",
      "speaker",
      "LinkedIn",
      "Instagram",
      "portfolio",
      "personal brand",
      "referral",
    ],
    valueProps: [
      "More polished expert presence",
      "Photos for profiles, proposals, and talks",
      "A visual library that supports referrals",
    ],
  },
  {
    id: "content",
    label: "Monthly Content Day",
    angle: "Recurring photo content for local businesses that sell through trust and visibility",
    subject: "A local content shoot for {{company}}",
    idealAccounts: [
      "Realtors and teams posting weekly",
      "Fitness, wellness, beauty, and med spa brands",
      "Restaurants, cafes, venues, and boutiques",
      "Service businesses running seasonal promotions",
    ],
    triggers: [
      "Instagram",
      "Facebook",
      "event",
      "open house",
      "seasonal",
      "promotion",
      "new service",
      "new listing",
      "campaign",
      "content",
    ],
    valueProps: [
      "A bank of local images for the month",
      "Less scramble for social posts",
      "Fresh photos tied to current offers",
    ],
  },
];

export const sampleLeads: Lead[] = [
  {
    id: "lead-sugar-hill-homes",
    company: "Sugar Hill Homes by Kayla",
    website: "https://example.com/sugar-hill-homes",
    industry: "Residential realtor",
    city: "Sugar Hill",
    state: "GA",
    contactName: "Kayla Brooks",
    title: "Realtor",
    email: "kayla@example.com",
    phone: "",
    linkedin: "",
    notes: "Active on Instagram, current profile photo looks casual, no branded neighborhood photos.",
    signals: "realtor, Instagram, headshot, neighborhood content",
    source: "Sample",
    status: "ready",
  },
  {
    id: "lead-duluth-property-group",
    company: "Duluth Property Group",
    website: "https://example.com/duluth-property",
    industry: "Real estate team",
    city: "Duluth",
    state: "GA",
    contactName: "Marcus Lee",
    title: "Team Lead",
    email: "marcus@example.com",
    phone: "",
    linkedin: "",
    notes: "Team page has mismatched headshots and no lifestyle images for listing presentations.",
    signals: "real estate team, listing presentation, team page, outdated headshots",
    source: "Sample",
    status: "new",
  },
  {
    id: "lead-peachtree-corners-mortgage",
    company: "Peachtree Corners Mortgage Co.",
    website: "https://example.com/pc-mortgage",
    industry: "Mortgage broker",
    city: "Peachtree Corners",
    state: "GA",
    contactName: "Nina Patel",
    title: "Owner",
    email: "nina@example.com",
    phone: "",
    linkedin: "",
    notes: "Referral-heavy business; website uses stock photos and a dated owner portrait.",
    signals: "mortgage, referral partners, stock photos, owner portrait",
    source: "Sample",
    status: "sent",
    lastTouch: "2026-05-17",
  },
  {
    id: "lead-lawrenceville-insurance",
    company: "Lawrenceville Family Insurance",
    website: "https://example.com/lawrenceville-insurance",
    industry: "Insurance agency",
    city: "Lawrenceville",
    state: "GA",
    contactName: "Teresa Morgan",
    title: "Agency Owner",
    email: "teresa@example.com",
    phone: "",
    linkedin: "",
    notes: "Google reviews are strong, but website and profile imagery are generic.",
    signals: "Google reviews, owner-led, generic website photos",
    source: "Sample",
    status: "replied",
    lastTouch: "2026-05-16",
  },
  {
    id: "lead-suwanee-organizing",
    company: "Suwanee Home Organizing",
    website: "https://example.com/suwanee-organizing",
    industry: "Home organizer",
    city: "Suwanee",
    state: "GA",
    contactName: "Brianna Hall",
    title: "Founder",
    email: "brianna@example.com",
    phone: "",
    linkedin: "",
    notes: "Great before-and-after service, but needs polished working portraits and process images.",
    signals: "before and after, founder, service page, Instagram",
    source: "Sample",
    status: "booked",
    lastTouch: "2026-05-14",
  },
  {
    id: "lead-snellville-fitness",
    company: "Snellville Strength Lab",
    website: "https://example.com/snellville-strength",
    industry: "Fitness studio",
    city: "Snellville",
    state: "GA",
    contactName: "Jordan Miles",
    title: "Owner",
    email: "jordan@example.com",
    phone: "",
    linkedin: "",
    notes: "Posting short clips and promotions, but still using launch-day photos across the site.",
    signals: "promotion, social, outdated photos, local studio",
    source: "Sample",
    status: "research",
  },
  {
    id: "lead-buford-consulting",
    company: "Buford Growth Consulting",
    website: "",
    industry: "Freelance consultant",
    city: "Buford",
    state: "GA",
    contactName: "",
    title: "",
    email: "",
    phone: "",
    linkedin: "",
    notes: "LinkedIn lead from local networking group. Needs website and email research.",
    signals: "consultant, LinkedIn, referral-based",
    source: "Sample",
    status: "research",
  },
];

export const serviceStack = [
  {
    stage: "Local business source",
    primary: "Google Maps, local chambers, and Gwinnett city directories",
    fallback: "Manual neighborhood list building",
    note: "Start with Lawrenceville, Duluth, Suwanee, Peachtree Corners, Sugar Hill, Buford, Snellville, and Norcross.",
  },
  {
    stage: "Realtor source",
    primary: "Brokerage sites, Zillow profiles, Realtor.com, Instagram, LinkedIn",
    fallback: "Open house signs and local referral groups",
    note: "Prioritize agents with active listings, reviews, social posting, and weak personal brand photos.",
  },
  {
    stage: "Contact enrichment",
    primary: "Apollo, Hunter, or manual website research",
    fallback: "Instagram DM or contact form for solo operators",
    note: "For small businesses, direct owner emails and Instagram handles can be more useful than corporate databases.",
  },
  {
    stage: "Verification",
    primary: "ZeroBounce, Bouncer, or sender verification",
    fallback: "Manual check before adding to a campaign",
    note: "Keep bounce rate low; local domains and Gmail addresses need extra care.",
  },
  {
    stage: "Outbound sender",
    primary: "Smartlead or Instantly",
    fallback: "Manual Gmail follow-up for high-value agents",
    note: "Keep volume modest and make the outreach feel local, specific, and human.",
  },
];
